import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedComplianceCaseRecord, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-compliance-case-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, SelectModule, CheckboxModule, CustomerSelectorComponent, AinDatePipe, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('sales.regulated.compliance.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <div class="layout">
          <section class="main">
            <header class="card"><a class="back-link" [routerLink]="['/sales/compliance-cases']">{{ t('catalog.action.backToList') }}</a></header>
            @if (isScreenMode()) {
              <section class="card grid">
                <label><span>{{ t('sales.regulated.compliance.fields.subjectType') }}</span><input pInputText [(ngModel)]="screenForm.subjectType" /></label>
                <label><span>{{ t('sales.regulated.compliance.fields.subjectId') }}</span><input pInputText [(ngModel)]="screenForm.subjectId" /></label>
                <label><span>{{ t('sales.regulated.compliance.fields.screening') }}</span>
                  <p-select [options]="screeningTypes" optionValue="value" [(ngModel)]="screenForm.screeningType" appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                    <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                  </p-select>
                </label>
                <label class="full"><p-checkbox [(ngModel)]="screenForm.simulateHardStop" [binary]="true" /><span>{{ t('sales.regulated.compliance.fields.simulateHardStop') }}</span></label>
                <button pButton type="button" [label]="t('sales.regulated.compliance.runScreen')" (click)="runScreen()"></button>
              </section>
            } @else if (isSuitabilityMode()) {
              <section class="card grid">
                <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="suitabilityForm.customerAccountId" /></label>
                <label><span>{{ t('sales.regulated.compliance.fields.productCategory') }}</span>
                  <p-select [options]="productCategories" optionLabel="value" optionValue="value" [(ngModel)]="suitabilityForm.productCategory" appendTo="body" />
                </label>
                <label><span>{{ t('sales.regulated.compliance.fields.outcome') }}</span>
                  <p-select [options]="suitabilityOutcomes" optionValue="value" [(ngModel)]="suitabilityForm.outcome" appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                    <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                  </p-select>
                </label>
                <label class="full"><span>{{ t('sales.regulated.compliance.fields.notes') }}</span><input pInputText [(ngModel)]="suitabilityForm.notes" /></label>
                <button pButton type="button" [label]="t('common.save')" (click)="saveSuitability()"></button>
              </section>
            } @else if (item(); as c) {
              <section class="summary card">
                <article><span>{{ t('sales.regulated.compliance.fields.screening') }}</span><strong>{{ c.screeningType }}</strong></article>
                <article><span>{{ t('sales.regulated.compliance.fields.result') }}</span><strong>{{ c.result }}</strong></article>
                <article><span>{{ t('sales.regulated.compliance.fields.hardStop') }}</span><strong>{{ c.isHardStop ? t('common.yes') : t('common.no') }}</strong></article>
                <article><span>{{ t('columns.createdAt') }}</span><strong>{{ c.createdAtUtc | ainDate:'datetime' }}</strong></article>
              </section>
              <p class="card">{{ c.decisionNotes }}</p>
              <p class="card">{{ t('sales.regulated.compliance.fields.subjectType') }}: {{ c.subjectType }} / {{ c.subjectId }}</p>
            }
          </section>
          @if (!isScreenMode() && !isSuitabilityMode() && item()) {
            <ain-sales-ai-copilot-panel entityType="ComplianceScreeningEvent" [entityId]="item()!.id" />
          }
        </div>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesComplianceCaseDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isScreenMode = signal(false);
  readonly isSuitabilityMode = signal(false);
  readonly item = signal<RegulatedComplianceCaseRecord | null>(null);
  screeningTypes = [
    { value: 'KYC', labelKey: 'sales.regulated.compliance.screening.kyc' },
    { value: 'KYB', labelKey: 'sales.regulated.compliance.screening.kyb' },
    { value: 'AML', labelKey: 'sales.regulated.compliance.screening.aml' },
    { value: 'Sanctions', labelKey: 'sales.regulated.compliance.screening.sanctions' }
  ];
  screenForm = { subjectType: 'Customer', subjectId: '', screeningType: 'AML', simulateHardStop: false };
  suitabilityForm = { customerAccountId: '' as string, productCategory: 'Gold', outcome: 'Approved', notes: '' };
  productCategories = [
    { value: 'Gold' },
    { value: 'Brokerage' },
    { value: 'Insurance' },
    { value: 'Fx' }
  ];
  suitabilityOutcomes = [
    { value: 'Approved', labelKey: 'sales.regulated.compliance.outcome.approved' },
    { value: 'Pending', labelKey: 'sales.regulated.compliance.outcome.pending' },
    { value: 'Rejected', labelKey: 'sales.regulated.compliance.outcome.rejected' }
  ];
  private returnUrl = '/sales/compliance-cases';
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('caseId') ?? '';
    const mode = this.route.snapshot.data['mode'] as string | undefined;
    const qp = this.route.snapshot.queryParamMap;
    this.isSuitabilityMode.set(mode === 'suitability');
    this.isScreenMode.set(!this.isSuitabilityMode() && (!id || id === 'new'));
    if (this.isSuitabilityMode()) {
      const customerId = qp.get('customerAccountId');
      const category = qp.get('productCategory');
      const returnUrl = qp.get('returnUrl');
      if (customerId) this.suitabilityForm.customerAccountId = customerId;
      if (category) this.suitabilityForm.productCategory = category;
      if (returnUrl) this.returnUrl = returnUrl;
    }
    if (this.isScreenMode() || this.isSuitabilityMode()) { this.loading.set(false); return; }
    this.api.getComplianceCase(id).subscribe({
      next: c => { this.item.set(c); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }
  pageTitle(): string {
    if (this.isSuitabilityMode()) return this.t('sales.regulated.compliance.recordSuitability');
    if (this.isScreenMode()) return this.t('sales.regulated.compliance.runScreen');
    return this.t('sales.regulated.compliance.detailTitle');
  }
  runScreen(): void {
    this.api.runComplianceScreen(this.screenForm).subscribe({
      next: () => void this.router.navigate(['/sales/compliance-cases'])
    });
  }
  saveSuitability(): void {
    if (!this.suitabilityForm.customerAccountId) return;
    this.api.recordSuitability(this.suitabilityForm).subscribe({
      next: () => {
        const qp = this.route.snapshot.queryParamMap;
        const quoteId = qp.get('quoteId');
        void this.router.navigate([this.returnUrl], { queryParams: quoteId ? { quoteId } : undefined });
      }
    });
  }
  t(key: string): string { return this.localization.translate(key); }
}
